import React, { useContext, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from 'reducer';
import { openJoinGroupModal, openPostModal } from 'dispatcher/modalDispatcher';
import { useSelectFollowedGroupListByJoinedAt } from 'entity/group/groupHooks';
import { X as CloseButton } from 'react-feather';
import useIsMobile from 'utils/hooks/useIsMobile';
import dayjs from 'dayjs';
import isEmpty from 'lodash/isEmpty';
import sortBy from 'lodash/sortBy';
import clsx from 'clsx';
import CtaCard from './CtaCard';
import { CommunityPageContext } from '../../../context/CommunityPageContext';

const NewUserOnboardCard = (props: { onScrollClick: () => void }) => {
  const { onScrollClick } = props;

  const dispatch = useDispatch();
  const isMobile = useIsMobile();
  const [showComplete, setShowComplete] = useState(false);
  const userProfile = useSelector((state: RootState) => state.profile.user);
  const { pageType } = useContext(CommunityPageContext);
  const followedGroups = useSelectFollowedGroupListByJoinedAt();

  const cardProps = [
    {
      title: 'Join 3 groups',
      description: 'See relevant content as you join more groups',
      onClick: () => dispatch(openJoinGroupModal({ isSigningUp: false })),
      isCompleted: followedGroups.length >= 3,
    },
    {
      title: 'Leave a comment',
      description: 'Engage with users by commenting on a post',
      onClick: onScrollClick,
      isCompleted: userProfile?.commentsCount > 0,
    },
    {
      title: 'Start a discussion',
      description: 'Share your thoughts with like-minded users',
      onClick: () => dispatch(openPostModal()),
      isCompleted: userProfile?.questionsCount > 0,
    },
  ];

  const CtaCards = sortBy(cardProps, ['isCompleted']).map(prop => {
    return (
      <CtaCard
        key={prop.title}
        title={prop.title}
        description={prop.description}
        onClick={prop.onClick}
        isCompleted={prop.isCompleted}
      />
    );
  });
  const totalCompleted = cardProps.filter(prop => prop.isCompleted).length;
  useEffect(() => {
    const startedDate = localStorage.getItem('community_get_started');

    if (!startedDate && totalCompleted > 0 && totalCompleted < 3) {
      localStorage.setItem('community_get_started', new Date().toDateString());
    }
    if (startedDate && totalCompleted === 3) {
      setShowComplete(true);

      // auto remove the alert after 5 minutes
      if (dayjs(new Date()).diff(startedDate, 'minute') > 5) {
        localStorage.removeItem('community_get_started');
      }
    }
  }, [totalCompleted]);

  if (showComplete) {
    const title =
      '🚀 Keep learning with the community and earn rewards as you do so!';
    return (
      <div className="rounded-none lg:rounded-lg w-full px-2 py-3  bg-green-200">
        <div className="flex w-full justify-between">
          <p className="text-sm font-bold">{title}</p>
          <CloseButton
            size={16}
            onClick={() => {
              setShowComplete(false);
              localStorage.removeItem('community_get_started');
            }}
            className="cursor-pointer min-h-[16px] min-w-[16px]"
          />
        </div>
      </div>
    );
  }

  if (totalCompleted === 3) return null;
  if (isEmpty(userProfile)) return null;
  if (pageType === 'trending' && followedGroups.length > 0) return null;

  return (
    <div className="rounded-none lg:rounded-lg w-full px-2 py-3  bg-green-200">
      <div className="flex flex-col gap-2 w-full">
        <p className="text-sm font-bold">
          <span aria-label="light-bulb" role="img">
            💡
          </span>{' '}
          {`Tips to get started in the community (${totalCompleted}/3)`}
        </p>
        <div
          className={clsx(
            'flex items-center w-full gap-2',
            isMobile && 'overflow-scroll',
          )}
        >
          {CtaCards}
        </div>
      </div>
    </div>
  );
};

export default NewUserOnboardCard;
